import { z } from 'zod';
import { LOYALTY_CODE, SUPPLIER_LOYALTY_CODE } from './loyalty_codes';

/**
 * Catálogo ESTÁTICO de programas de fidelidade que a tela de Preferências
 * oferece (spec 2026-09-17). `codes[0]` é o código que gravamos (legado
 * `mileagePlans.airline.code` / store `chainCode`) e que a Sabre recebe; os
 * demais códigos existem só para a busca ("JJ" acha LATAM Pass) e para
 * resolver uma linha antiga gravada com um código secundário. `popular`
 * = aparece antes de qualquer digitação. Logos: aéreo pelo IATA (CDN dos cards
 * de voo); hotel e locadora por `id` no web-chat (`ui/preferences/loyalty_logos`).
 */
export const LoyaltyProgramKindSchema = z.enum(['air', 'hotel', 'rental']);
export type LoyaltyProgramKind = z.infer<typeof LoyaltyProgramKindSchema>;

export const LoyaltyProgramSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
    kind: LoyaltyProgramKindSchema,
    name: z.string().min(1).max(60),
    owner: z.string().min(1).max(80),
    codes: z.array(z.string().regex(SUPPLIER_LOYALTY_CODE)).min(1).max(12),
    popular: z.boolean(),
  })
  .strict()
  // Aéreo é sempre IATA (2 chars); a chave própria longa é só de hotel e locadora.
  .refine((p) => p.kind !== 'air' || p.codes.every((c) => LOYALTY_CODE.test(c)), {
    path: ['codes'],
    message: 'air codes are 2-char IATA',
  });
export type LoyaltyProgram = z.infer<typeof LoyaltyProgramSchema>;

/** Atalhos do catálogo abaixo: um por tipo, `popular` falso por padrão. */
const air = (id: string, name: string, owner: string, codes: string[], popular = false) =>
  ({ id, kind: 'air', name, owner, codes, popular }) as const satisfies LoyaltyProgram;
const hotel = (id: string, name: string, owner: string, codes: string[], popular = false) =>
  ({ id, kind: 'hotel', name, owner, codes, popular }) as const satisfies LoyaltyProgram;
const rental = (id: string, name: string, owner: string, codes: string[], popular = false) =>
  ({ id, kind: 'rental', name, owner, codes, popular }) as const satisfies LoyaltyProgram;

export const LOYALTY_PROGRAMS: readonly LoyaltyProgram[] = [
  // ── aéreo: Brasil e vizinhos primeiro ──
  // Revisado em 25/09/2026: Volare (ITA) acabou em 30/03/2026 e a ITA entrou no
  // Miles & More — AZ NÃO vira código do Miles & More: o número do Volare não é
  // conta do Miles & More, e a linha antiga com AZ segue com o nome gravado; TudoAzul virou Azul Fidelidade; Mileage Plan + HawaiianMiles
  // viraram Atmos Rewards; Iberia Plus virou Iberia Club; Asia Miles é a moeda
  // do programa "Cathay". Companhia sem programa próprio entra como código
  // secundário do programa em que credita (Fiji → AAdvantage, flydubai → Skywards).
  air('latam-pass', 'LATAM Pass', 'LATAM Airlines', ['LA', 'JJ', 'LP', 'XL', '4C', 'PZ'], true),
  air('smiles', 'Smiles', 'GOL Linhas Aéreas', ['G3'], true),
  air('azul-fidelidade', 'Azul Fidelidade', 'Azul Linhas Aéreas', ['AD'], true),
  air('aerolineas-plus', 'Aerolíneas Plus', 'Aerolíneas Argentinas', ['AR'], true),
  air('lifemiles', 'LifeMiles', 'Avianca', ['AV'], true),
  air('connectmiles', 'ConnectMiles', 'Copa Airlines', ['CM'], true),
  air('aeromexico-rewards', 'Aeroméxico Rewards', 'Aeroméxico', ['AM', '5D'], true),
  air('sky-plus', 'SKY Plus', 'SKY Airline', ['H2', 'H8']),
  air('boa-elevate', 'Elévate', 'Boliviana de Aviación', ['OB']),
  // ── América do Norte e Caribe ──
  air('aadvantage', 'AAdvantage', 'American Airlines', ['AA', 'FJ', 'JA'], true),
  air('mileageplus', 'MileagePlus', 'United Airlines', ['UA'], true),
  air('skymiles', 'SkyMiles', 'Delta Air Lines', ['DL'], true),
  air('aeroplan', 'Aeroplan', 'Air Canada', ['AC']),
  air('atmos-rewards', 'Atmos Rewards', 'Alaska Airlines · Hawaiian Airlines', ['AS', 'HA']),
  air('trueblue', 'TrueBlue', 'JetBlue', ['B6']),
  air('rapid-rewards', 'Rapid Rewards', 'Southwest Airlines', ['WN']),
  air('frontier-miles', 'FRONTIER Miles', 'Frontier Airlines', ['F9']),
  air('westjet-rewards', 'WestJet Rewards', 'WestJet', ['WS']),
  air('viporter', 'VIPorter', 'Porter Airlines', ['PD']),
  air('caribbean-miles', 'Caribbean Miles', 'Caribbean Airlines', ['BW']),
  air('sir-turtle-rewards', 'Sir Turtle Rewards', 'Cayman Airways', ['KX']),
  air('bahamasair-flyer', 'Bahamasair Flyer', 'Bahamasair', ['UP']),
  // ── Europa ──
  air('flying-blue', 'Flying Blue', 'Air France · KLM', ['AF', 'KL', 'RO', 'HV', 'TO'], true),
  air(
    'miles-and-more',
    'Miles & More',
    'Lufthansa Group',
    ['LH', 'LX', 'OS', 'SN', 'LO', 'EW', 'WK', 'EN', '4Y', 'OU', 'LG'],
    true,
  ),
  air('british-airways-club', 'British Airways Club', 'British Airways', ['BA']),
  air('iberia-club', 'Iberia Club', 'Iberia', ['IB'], true),
  air('aerclub', 'AerClub', 'Aer Lingus', ['EI']),
  air('finnair-plus', 'Finnair Plus', 'Finnair', ['AY']),
  air('vueling-club', 'Vueling Club', 'Vueling', ['VY']),
  air('tap-miles-and-go', 'TAP Miles&Go', 'TAP Air Portugal', ['TP'], true),
  air('flying-club', 'Flying Club', 'Virgin Atlantic', ['VS']),
  air('eurobonus', 'EuroBonus', 'SAS', ['SK']),
  air('miles-and-smiles', 'Miles&Smiles', 'Turkish Airlines', ['TK']),
  air('miles-plus-bonus', 'Miles+Bonus', 'Aegean Airlines', ['A3']),
  air('suma', 'SUMA', 'Air Europa', ['UX']),
  air('saga-club', 'Saga Club', 'Icelandair', ['FI']),
  air('norwegian-reward', 'Norwegian Reward', 'Norwegian', ['DY', 'D8']),
  air('airbaltic-club', 'airBaltic Club', 'airBaltic', ['BT']),
  air('km-rewards', 'KM Rewards', 'KM Malta Airlines', ['KM']),
  air('air-serbia-elevate', 'Elevate', 'Air Serbia', ['JU']),
  air('pegasus-bolbol', 'Pegasus BolBol', 'Pegasus Airlines', ['PC']),
  air('preference', 'Préférence', 'Air Caraïbes · French bee', ['TX', 'BF']),
  air('corsair-club', 'Corsair Club', 'Corsair', ['SS']),
  // ── Oriente Médio e Ásia Central ──
  air('skywards', 'Skywards', 'Emirates', ['EK', 'FZ'], true),
  air('privilege-club', 'Privilege Club', 'Qatar Airways', ['QR']),
  air('etihad-guest', 'Etihad Guest', 'Etihad Airways', ['EY', 'HM']),
  air('alfursan', 'AlFursan', 'Saudia', ['SV']),
  air('sfeer', 'Sfeer', 'Riyadh Air', ['RX']),
  air('nasmiles', 'nasmiles', 'flynas', ['XY']),
  air('falconflyer', 'Falconflyer', 'Gulf Air', ['GF']),
  air('oasis-club', 'Oasis Club', 'Kuwait Airways', ['KU']),
  air('sindbad', 'Sindbad', 'Oman Air', ['WY']),
  air('royal-club', 'Royal Club', 'Royal Jordanian', ['RJ']),
  air('cedar-miles', 'Cedar Miles', 'Middle East Airlines', ['ME']),
  air('matmid', 'Matmid', 'El Al', ['LY']),
  air('airrewards', 'AirRewards', 'Air Arabia', ['G9']),
  air('nomad-club', 'Nomad Club', 'Air Astana', ['KC']),
  air('uzairplus', 'UzAirPlus', 'Uzbekistan Airways', ['HY']),
  air('azal-miles', 'AZAL Miles', 'Azerbaijan Airlines', ['J2']),
  // ── Ásia e Oceania ──
  air('krisflyer', 'KrisFlyer', 'Singapore Airlines', ['SQ', 'TR']),
  air('asia-miles', 'Cathay (Asia Miles)', 'Cathay Pacific', ['CX']),
  air('ana-mileage-club', 'ANA Mileage Club', 'All Nippon Airways', ['NH']),
  air('jal-mileage-bank', 'JAL Mileage Bank', 'Japan Airlines', ['JL']),
  air('skypass', 'SKYPASS', 'Korean Air', ['KE']),
  // Asiana Club funde no SKYPASS em 17/12/2026; depois disso, OZ vai para o SKYPASS.
  air('asiana-club', 'Asiana Club', 'Asiana Airlines', ['OZ']),
  air('infinity-mileagelands', 'Infinity MileageLands', 'EVA Air', ['BR']),
  air('dynasty-flyer', 'Dynasty Flyer', 'China Airlines', ['CI', 'AE']),
  air('cosmile', 'COSMILE', 'Starlux Airlines', ['JX']),
  air('phoenixmiles', 'PhoenixMiles', 'Air China', ['CA', 'ZH']),
  air('eastern-miles', 'Eastern Miles', 'China Eastern', ['MU']),
  air('sky-pearl-club', 'Sky Pearl Club', 'China Southern', ['CZ']),
  air('fortune-wings-club', 'Fortune Wings Club', 'Hainan Airlines', ['HU']),
  air('egret-club', 'Egret Club', 'Xiamen Airlines', ['MF']),
  air('golden-panda', 'Golden Panda', 'Sichuan Airlines', ['3U']),
  air('juneyao-club', 'Juneyao Air Club', 'Juneyao Air', ['HO']),
  air('royal-orchid-plus', 'Royal Orchid Plus', 'Thai Airways', ['TG']),
  air('flyerbonus', 'FlyerBonus', 'Bangkok Airways', ['PG']),
  air('enrich', 'Enrich', 'Malaysia Airlines', ['MH']),
  air('airasia-rewards', 'airasia rewards', 'AirAsia', ['AK', 'D7', 'FD', 'QZ', 'Z2']),
  air('garudamiles', 'GarudaMiles', 'Garuda Indonesia', ['GA']),
  air('lotusmiles', 'Lotusmiles', 'Vietnam Airlines', ['VN']),
  air('mabuhay-miles', 'Mabuhay Miles', 'Philippine Airlines', ['PR', '2P']),
  air('go-rewards', 'Go Rewards', 'Cebu Pacific', ['5J', 'DG']),
  air('maharaja-club', 'Maharaja Club', 'Air India', ['AI', 'IX']),
  air('bluchip', 'IndiGo BluChip', 'IndiGo', ['6E']),
  air('flysmiles', 'FlySmiLes', 'SriLankan Airlines', ['UL']),
  air('pia-awards-plus', 'PIA Awards+', 'Pakistan International Airlines', ['PK']),
  air('biman-loyalty-club', 'Biman Loyalty Club', 'Biman Bangladesh Airlines', ['BG']),
  air('qantas-frequent-flyer', 'Qantas Frequent Flyer', 'Qantas', ['QF', 'JQ', '3K', 'GK']),
  air('velocity', 'Velocity Frequent Flyer', 'Virgin Australia', ['VA']),
  air('airpoints', 'Airpoints', 'Air New Zealand', ['NZ']),
  air('club-tiare', 'Club Tiare', 'Air Tahiti Nui', ['TN']),
  air('destinations', 'Destinations', 'Air Niugini', ['PX']),
  // ── África ──
  air('shebamiles', 'ShebaMiles', 'Ethiopian Airlines', ['ET']),
  air('voyager', 'Voyager', 'South African Airways', ['SA']),
  air('safar-flyer', 'Safar Flyer', 'Royal Air Maroc', ['AT']),
  air('egyptair-plus', 'EgyptAir Plus', 'EgyptAir', ['MS']),
  air('asante-rewards', 'Asante Rewards', 'Kenya Airways', ['KQ']),
  air('umbi-umbi', 'Umbi Umbi', 'TAAG Angola Airlines', ['DT']),
  air('kestrelflyer', 'Kestrelflyer', 'Air Mauritius', ['MK']),
  air('dreammiles', 'DreamMiles', 'RwandAir', ['WB']),
  air('smiles-air-cote-divoire', 'sMiles', "Air Côte d'Ivoire", ['HF']),
  // ── hotel: códigos confirmados na lista oficial da Sabre (CSL API Reference Data, mai/2025) ──
  // Revisado em 25/09/2026: Iberostar Horizons acabou (jun/2023) e o Iberostar
  // credita no IHG — IR fica fora do IHG pelo mesmo motivo do AZ no aéreo;
  // Radisson Rewards Americas virou Choice Privileges (jul/2023),
  // então Prize/Park Plaza/Park Inn das Américas saem do Radisson para a Choice.
  // Programa sem chain code na Sabre entra com chave própria de 3–12 chars
  // (`SUPPLIER_LOYALTY_CODE`): guardamos o número, mas ele nunca vai para a Sabre.
  hotel(
    'marriott-bonvoy',
    'Marriott Bonvoy',
    'Marriott International',
    ['EM', 'MC', 'CY', 'RZ', 'SI', 'WI', 'BR', 'DS'],
    true,
  ),
  hotel(
    'hilton-honors',
    'Hilton Honors',
    'Hilton',
    ['EH', 'HH', 'GI', 'HG', 'PY', 'RU', 'UA'],
    true,
  ),
  hotel('world-of-hyatt', 'World of Hyatt', 'Hyatt', ['LH', 'HY', 'HU'], true),
  hotel(
    'ihg-one-rewards',
    'IHG One Rewards',
    'IHG Hotels & Resorts',
    ['6C', 'IC', 'HI', 'CP'],
    true,
  ),
  hotel(
    'all-accor',
    'ALL – Accor Live Limitless',
    'Accor',
    ['RT', 'SB', 'PU', 'FA', 'SL', 'AE'],
    true,
  ),
  hotel(
    'wyndham-rewards',
    'Wyndham Rewards',
    'Wyndham Hotels & Resorts',
    ['WR', 'WY', 'RA', 'DI', 'LQ', 'WT'],
    true,
  ),
  hotel(
    'choice-privileges',
    'Choice Privileges',
    'Choice Hotels',
    ['EC', 'CI', 'CZ', 'CC', 'QI', 'SZ', 'CR', 'PJ', 'PQ', 'PZ'],
    true,
  ),
  hotel('best-western-rewards', 'Best Western Rewards', 'BWH Hotels', ['EW', 'BW']),
  // CS (Country Inn) fica SÓ aqui: o código é das duas redes (Choice nas Américas),
  // e em duas listas `programByCode` resolveria uma linha do Radisson como Choice.
  hotel('radisson-rewards', 'Radisson Rewards', 'Radisson Hotel Group', [
    'CW',
    'RD',
    'PD',
    'PK',
    'CS',
  ]),
  hotel('melia-rewards', 'MeliáRewards', 'Meliá Hotels International', ['ME'], true),
  hotel('gha-discovery', 'GHA Discovery', 'Global Hotel Alliance', [
    'GA',
    'NH',
    'KI',
    'NS',
    'MW',
    'OH',
    'NT',
    'PF',
    'PL',
    'IA',
    'LR',
    'MH',
  ]),
  hotel('i-prefer', 'I Prefer Hotel Rewards', 'Preferred Hotels & Resorts', ['PV', 'PH']),
  hotel('leaders-club', 'Leaders Club', 'The Leading Hotels of the World', ['LW']),
  hotel('slh-club', 'SLH Club', 'Small Luxury Hotels of the World', ['LX']),
  hotel('select-guest', 'Select Guest', 'Omni Hotels & Resorts', ['OM']),
  hotel('shangri-la-circle', 'Shangri-La Circle', 'Shangri-La', ['SG']),
  hotel('my-barcelo', 'my Barceló', 'Barceló Hotel Group', ['BN']),
  hotel('sonesta-travel-pass', 'Sonesta Travel Pass', 'Sonesta', ['SN']),
  hotel('fiesta-rewards', 'Fiesta Rewards', 'Grupo Posadas', ['FH', 'YP']),
  hotel('star-traveler', 'Star Traveler', 'Eurostars Hotel Company', ['HA']),
  hotel('h-rewards', 'H Rewards', 'H World International', ['SR']),
  hotel('leonardo-advantage-club', 'Leonardo AdvantageCLUB', 'Leonardo Hotels', ['LD']),
  hotel('scandic-friends', 'Scandic Friends', 'Scandic', ['SH']),
  hotel('jumeirah-one', 'Jumeirah One', 'Jumeirah', ['JT']),
  hotel('rotana-rewards', 'Rotana Rewards', 'Rotana', ['RO']),
  hotel('taj-innercircle-neupass', 'Taj InnerCircle – NeuPass', 'IHCL', ['TJ']),
  hotel('oberoi-one', 'Oberoi One', 'The Oberoi Group', ['OB']),
  hotel('club-itc', 'Club ITC', 'ITC Hotels', ['IZ']),
  hotel('dusit-gold', 'Dusit Gold', 'Dusit International', ['DW']),
  hotel('one-harmony', 'One Harmony', 'Okura Nikko Hotels', ['OC', 'NK']),
  hotel('lotte-hotel-rewards', 'Lotte Hotel Rewards', 'Lotte Hotels', ['LL']),
  hotel('ascott-star-rewards', 'Ascott Star Rewards', 'The Ascott Limited', ['AZ']),
  hotel('fraser-world', 'Fraser World', 'Frasers Hospitality', ['FI']),
  hotel('mgm-rewards', 'MGM Rewards', 'MGM Resorts', ['MV']),
  hotel('caesars-rewards', 'Caesars Rewards', 'Caesars Entertainment', ['HR']),
  hotel('unity-by-hard-rock', 'Unity by Hard Rock', 'Hard Rock International', ['RK']),
  hotel('drury-rewards', 'Drury Rewards', 'Drury Hotels', ['DR']),
  hotel('fans-of-mo', 'Fans of M.O.', 'Mandarin Oriental', ['MO']),
  hotel('island-insiders-club', 'Island Insiders Club', 'Sandals Resorts', ['JS', 'JB']),
  hotel('redirewards', 'RediRewards', 'Red Roof', ['RF']),
  hotel('my6', 'My6', 'G6 Hospitality (Motel 6)', ['MX']),
  // ── hotel sem código na Sabre (chave própria; só guardamos o número) ──
  hotel(
    'lets-atlantica',
    "Let's Atlantica",
    'Atlantica Hospitality International',
    ['ATLANTICA'],
    true,
  ),
  hotel('my-wish', 'My Wish', 'Grupo Wish', ['MYWISH']),
  hotel('vila-gale-club', 'Vila Galé Club', 'Vila Galé', ['VILAGALE']),
  hotel('uainn', 'UAInn', 'Nacional Inn Hotéis', ['UAINN']),
  hotel('fidelize-laghetto', 'Fidelize Laghetto', 'Rede Laghetto', ['LAGHETTO']),
  hotel('viero', 'Viero Clube de Vantagens', 'Slaviero Hotéis', ['VIERO']),
  hotel('palladium-rewards', 'Palladium Rewards', 'Palladium Hotel Group', ['PALLADIUM']),
  hotel('riu-class', 'RIU Class', 'RIU Hotels & Resorts', ['RIU']),
  hotel('h10-rewards', 'H10 Rewards', 'H10 Hotels', ['H10']),
  hotel('catalonia-rewards', 'Catalonia Rewards', 'Catalonia Hotels & Resorts', ['CATALONIA']),
  // ── locadoras (pesquisa de 08/10/2026) ──
  // `codes[0]` é o código de locadora da Sabre quando duas fontes o confirmam (lista
  // de códigos + guia GDS da própria marca); senão, chave própria de 3–12 chars, que
  // nunca vai à Sabre. Movida, Payless (ZA), Green Motion (GM, fonte de 2012) e
  // Woodford (WF, de 2013) ficaram na chave própria: se o código se confirmar, ele
  // entra como `codes[0]` e a chave fica como secundária, para as linhas gravadas
  // seguirem resolvendo. Cada marca tem o seu programa — Hertz/Dollar/Thrifty,
  // Avis/Budget/Payless/Maggiore e Enterprise/National/Alamo não compartilham conta;
  // Budget Saudi tem número próprio, separado do Fastbreak. Quem identifica o membro
  // pelo CPF ou pelo login (Movida, Foco…) entra igual: o viajante grava esse
  // identificador no campo de número. Fora: Unidas Sempre (parou de acumular em
  // 31/08/2026), Advantage Expressway (sem sinal de atividade em 2026), coalizões
  // (Livelo, Dotz, L.POINT) e intermediários (Rentcars, Rentalcars).
  rental('localiza-fidelidade', 'Localiza Fidelidade', 'Localiza', ['LL'], true),
  // `MO` é o `rental.id` da Movida no rental-car-api (a busca do chat): o card do
  // 1-Click casa a locadora da oferta por ele. Localiza já casa pelo `LL`.
  rental('movida-fidelidade', 'Fidelidade Movida', 'Movida', ['MOVIDA', 'MO'], true),
  rental('foco-volta-certa', 'Volta Certa', 'Foco Aluguel de Carros', ['FOCO']),
  rental('mitta-redpoints', 'Redpoints', 'Mitta', ['MITTA']),
  rental('hertz-gold-plus-rewards', 'Hertz Gold Plus Rewards', 'Hertz', ['ZE'], true),
  rental('avis-preferred', 'Avis Preferred', 'Avis', ['ZI'], true),
  rental('budget-fastbreak', 'Budget Fastbreak', 'Budget', ['ZD']),
  rental('enterprise-plus', 'Enterprise Plus', 'Enterprise', ['ET'], true),
  rental('emerald-club', 'Emerald Club', 'National', ['ZL'], true),
  rental('alamo-insiders', 'Alamo Insiders', 'Alamo', ['AL']),
  rental('sixt-one', 'SIXT ONE', 'Sixt', ['SX'], true),
  rental('europcar-privilege', 'Privilege For You', 'Europcar', ['EP']),
  rental('dollar-express-rewards', 'Dollar Express Rewards', 'Dollar', ['ZR']),
  rental('thrifty-blue-chip', 'Thrifty Blue Chip', 'Thrifty', ['ZT']),
  rental('payless-perks-club', 'Payless Perks Club', 'Payless', ['PAYLESS']),
  rental('fox-rewards', 'Fox Rewards', 'Fox Rent A Car', ['FX']),
  rental('ace-member-rewards', 'Ace Member Rewards', 'ACE Rent A Car', ['AC']),
  rental('drive-green', 'Drive Green', 'Green Motion', ['GREENMOTION']),
  rental('goldcar-club', 'Goldcar Club', 'Goldcar', ['GOLDCAR']),
  rental('maggiore-club', 'Maggiore Club', 'Maggiore', ['MAGGIORE']),
  rental('centauro-goldclub', 'GoldClub', 'Centauro', ['CENTAURO']),
  rental('ok-club', 'OK Club', 'OK Mobility', ['OKMOBILITY']),
  rental('mylocauto-friends', 'MyLocauto Friends', 'Locauto', ['LOCAUTO']),
  rental('times-club', 'Times Club', 'Times Car Rental', ['TIMES']),
  rental('toyota-rent-a-car-members', 'Toyota Rent a Car Members', 'Toyota Rent a Car', [
    'TOYOTARAC',
  ]),
  rental('nippon-rent-a-car-members', 'Nippon Rent-A-Car Members Club', 'Nippon Rent-A-Car', [
    'NIPPONRAC',
  ]),
  rental('orix-prime-members', 'Prime Members Club', 'ORIX Rent-A-Car', ['ORIX']),
  rental('car-inc-membership', 'CAR Inc Membership', 'CAR Inc (神州租车)', ['CARINC']),
  rental('budget-saudi-loyalty', 'Budget Saudi Loyalty Program', 'Budget Saudi', ['BUDGETKSA']),
  rental('yelo-amyali', 'Amyali', 'Yelo', ['YELO']),
  rental('woodford-advance', 'Woodford Advance', 'Woodford Car Hire', ['WOODFORD']),
];

export function loyaltyProgramsOf(kind: LoyaltyProgramKind): LoyaltyProgram[] {
  return LOYALTY_PROGRAMS.filter((p) => p.kind === kind);
}

export function programById(id: string): LoyaltyProgram | undefined {
  return LOYALTY_PROGRAMS.find((p) => p.id === id);
}

/** Código EXATO (caixa alta): a linha gravada já vem normalizada. */
export function programByCode(kind: LoyaltyProgramKind, code: string): LoyaltyProgram | undefined {
  const same = LOYALTY_PROGRAMS.filter((p) => p.kind === kind);
  return same.find((p) => p.codes[0] === code) ?? same.find((p) => p.codes.includes(code));
}
